import React, { useEffect, useRef, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PrevailingWageForm } from './Form';
import PrevailingWageTable from './PrevailingWageTable';
import Excel from '../../../assets/excel.png';
import {
  Breadcrumbs,
  Button,
  EdgeReportingWindow,
  ModalConfirmation,
  SubHeader,
  ReportLoader,
} from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData } from '../operations';

import './styles.scoped.css';

// Build CSV column defs from the first row's keys so an export ships every column the proc
// returns without hardcoding them. The on-screen table shows only the 14 design columns
// (prevailingWageColumns in PrevailingWageTable), but per PRST-120 both Excel exports must
// match their source sheets in full — Certified Payroll (tb_sp_pointsnorth, ~89 columns) and
// the Missing Job Data exception (tb_sp_pointsnorth_exception).
const columnsFromKeys = rows => (rows?.length ? Object.keys(rows[0]).map(key => ({ Header: key, accessor: key })) : []);

const PrevailingWageCriteria = ({ form, setForm, handleChange, handleViewResults, errors, loadingResults }) => {
  const navigate = useNavigate();
  return (
    <Form
      noValidate
      onSubmit={e => e.preventDefault()}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PrevailingWageForm form={form} setForm={setForm} handleChange={handleChange} errors={errors} />
      <div className="buttons-row">
        {/* Per SOP, keep the action enabled and validate on press (inline field errors);
            reserve disabled for the in-flight state only. */}
        <Button
          variant="primary"
          disabled={loadingResults}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

// Cut Off Date defaults to Period End Date + 9 days (PointsNorth convention); user-overridable.
// Parse by component (not new Date(iso)) to avoid the UTC-shift that moves the date a day.
export const addDays = (isoDate, days) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const PrevailingWage = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [form, setForm] = useState({ PeriodEndDate: '', CutOffDate: '', Exception: 'N' });
  const [data, setData] = useState([]);
  const [exceptionData, setExceptionData] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // Per-field validation errors, surfaced inline on View (SOP: no disabled gating, no alert).
  const [errors, setErrors] = useState({});
  // One "Export" control drives both CSVs via hidden CSVLinks (Certified Payroll + Exception).
  const certifiedCsvRef = useRef(null);
  const exceptionCsvRef = useRef(null);

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const handleChange = e => {
    const { name, value } = e.target;
    // Clear a field's validation error as soon as the user edits it.
    if (value && errors?.[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    // Selecting a Period End Date auto-fills Cut Off Date to +9 days; the user can still override it.
    if (name === 'PeriodEndDate') {
      setForm(prev => ({ ...prev, PeriodEndDate: value, CutOffDate: addDays(value, 9) }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const certifiedFileName = `edge-reporting-prevailing-wage-certified-payroll-${newDateFormatted.toString()}.csv`;
  const exceptionFileName = `edge-reporting-prevailing-wage-exception-${newDateFormatted.toString()}.csv`;

  // Single Export action downloads both files: Certified Payroll always, Exception when present.
  // The browser may ask to allow multiple downloads the first time.
  const handleExport = () => {
    certifiedCsvRef.current?.link?.click();
    if (exceptionData?.length) exceptionCsvRef.current?.link?.click();
  };

  // Export is gated behind an approval modal so the user knows how many files are coming —
  // the exception proc can legitimately return no rows, and the download must not be
  // skipped silently (PRST-123).
  const exportModalBody = exceptionData?.length
    ? 'This will download two files — the Certified Payroll report and the Missing Job Data exception report.'
    : 'The Missing Job Data exception report returned no data for this period. Only the Certified Payroll report will download.';

  const confirmExport = () => {
    setShowExportModal(false);
    handleExport();
  };

  const handleViewResults = async () => {
    // Validate on press and surface errors inline beneath each field (SOP — no disabled gating, no alert).
    const nextErrors = {};
    if (!form.PeriodEndDate) nextErrors.PeriodEndDate = 'Period End Date is required.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    setLoadingResults(true);

    // Certified Payroll criteria map to tb_sp_pointsnorth (@InPeriodEndDate, @CutoffDate, @Exception).
    // The report pulls all clients (no customer filter). @Exception comes from the Exception Type pick
    // list (N = paid before cutoff, Y = paid after cutoff). The Exception report
    // (tb_sp_pointsnorth_exception) takes only the period end date.
    const payload = {
      PeriodEndDate: form.PeriodEndDate,
      CutoffDate: form.CutOffDate || '',
      Exception: form.Exception || 'N',
    };
    const exceptionPayload = { PeriodEndDate: form.PeriodEndDate };

    try {
      // Certified Payroll (shown + export file 1) and the Missing Job Data exception
      // (export file 2 only) come from two separate procs.
      const [certifiedRes, exceptionRes] = await Promise.all([
        generateReport({ endpoint: 'pointsnorth-certified-payroll', payload }),
        generateReport({ endpoint: 'pointsnorth-exception', payload: exceptionPayload }),
      ]);

      const certifiedData = certifiedRes?.location ? await getReportData(certifiedRes.location) : [];
      const exceptionRows = exceptionRes?.location ? await getReportData(exceptionRes.location) : [];

      if (certifiedRes?.location) await removeReportData(certifiedRes.location);
      if (exceptionRes?.location) await removeReportData(exceptionRes.location);

      if (certifiedData?.length) {
        setData(certifiedData);
        setExceptionData(Array.isArray(exceptionRows) ? exceptionRows : []);
        setShowResults(true);
      } else {
        alert(certifiedRes?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
      alert('An error occurred while generating the report. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Prevailing Wage Report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Prevailing Wage'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'View Report' : 'Prevailing Wage'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <div className="export-links">
            {/* Hidden CSVLinks perform the downloads; the visible Export control triggers both. */}
            <CSVLink
              ref={certifiedCsvRef}
              className="export-hidden"
              filename={certifiedFileName}
              data={getCSVData(data, columnsFromKeys(data))}
            />
            <CSVLink
              ref={exceptionCsvRef}
              className="export-hidden"
              filename={exceptionFileName}
              data={getCSVData(exceptionData, columnsFromKeys(exceptionData))}
            />
            <button
              type="button"
              className="export-button"
              aria-label="Export"
              onClick={() => setShowExportModal(true)}>
              <ImageColorChanger src={Excel} className="export-icon" alt="export-prevailing-wage-to-csv" />
            </button>
            <ModalConfirmation
              isOpen={showExportModal}
              title="Export report files?"
              body={exportModalBody}
              primaryButton
              primaryButtonTitle="Download"
              onClick={confirmExport}
              optionalClose
              secondaryButtonTitle="Cancel"
              secondaryOnClick={() => setShowExportModal(false)}
            />
          </div>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <PrevailingWageTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button
              variant="primary"
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />
            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{ border: '2px solid #343a40', minHeight: 200 }}
          windowContent={
            <PrevailingWageCriteria
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleViewResults={handleViewResults}
              errors={errors}
              loadingResults={loadingResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default PrevailingWage;
